import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { clearAuth } from './authSlice';

export interface UserProfile {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  avatar: string;
  memberSince: string;
  accountNumber: string;
  lastLogin: string;
  loginCount: number;
  securityLevel: string;
  preferences: any;
  avatarBase64?: string;
  
  // New fields for account type identification
  accountType: 'PARENT' | 'CHILD'; // Account type from backend
  isParentAccount?: boolean; // Flag indicating if this is a parent account (expected field name)
  parentAccount?: boolean; // Flag indicating if this is a parent account (actual backend field name)
  hasChildAccounts: boolean; // Flag indicating if this parent has child accounts

  // SEPA limits and usage
  sepaDailyLimit?: string | number;
  sepaMonthlyLimit?: string | number;
  sepaYearlyLimit?: string | number;
  sepaSingleTransactionLimit?: string | number;
  sepaLimitCurrency?: string;
  sepaDailyUsage?: string | number;
  sepaMonthlyUsage?: string | number;
  sepaYearlyUsage?: string | number;
}

interface ProfileState {
  profile: UserProfile | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: ProfileState = {
  profile: null,
  isLoading: false,
  error: null,
};

export const fetchProfile = createAsyncThunk<UserProfile, void, { state: RootState; dispatch: any }>(
  'profile/fetchProfile',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState().auth;
      const token = state.token;
      const authType = state.type;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      // Only add Authorization header for JWT auth
      if (token && authType === 'JWT') {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${getApiBaseUrl()}/profile`, {
        headers,
        credentials: authType === 'SESSION' ? 'include' : 'omit'
      });
      
      const json = await response.json();
      if (!response.ok) {
        if (response.status === 401) {
          // Token is invalid/expired — clear auth so the app redirects to login
          dispatch(clearAuth());
          return rejectWithValue('Session expired. Please log in again.');
        }
        if (json.errors) return rejectWithValue(json.errors.join(', '));
        return rejectWithValue(json.message || 'Failed to fetch profile');
      }
      
      // Permissions are now stored in the database and returned by the backend
      const profileData = json.data;
      
      return profileData;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Unknown error');
    }
  }
);

export const updateProfile = createAsyncThunk<UserProfile, Partial<UserProfile>, { state: RootState }>(
  'profile/updateProfile',
  async (profileData, { getState, rejectWithValue }) => {
    try {
      const state = getState().auth;
      const token = state.token;
      const authType = state.type;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      // Only add Authorization header for JWT auth
      if (token && authType === 'JWT') {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${getApiBaseUrl()}/profile`, {
        method: 'PUT',
        headers,
        credentials: authType === 'SESSION' ? 'include' : 'omit',
        body: JSON.stringify(profileData),
      });
      const json = await response.json();
      if (!response.ok) {
        if (json.errors) return rejectWithValue(json.errors.join(', '));
        return rejectWithValue(json.message || 'Failed to update profile');
      }
      return json.data;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Unknown error');
    }
  }
);

export const uploadAvatar = createAsyncThunk<UserProfile, File, { state: RootState }>(
  'profile/uploadAvatar',
  async (file, { getState, rejectWithValue }) => {
    try {
      const state = getState().auth;
      const token = state.token;
      const authType = state.type;
      
      const headers: Record<string, string> = {};
      
      // Always send JWT token if available (backend can use JWT OR session cookies)
      if (token && token !== 'null' && token !== 'undefined') {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const formData = new FormData();
      formData.append('file', file); // Backend expects 'file' parameter name
      
      // CRITICAL: Always include credentials for cross-origin requests (localhost:3000 → api.zogzila.com)
      // Cookies are ONLY sent in cross-origin requests if credentials: 'include' is set
      // This allows Spring Security to authenticate with either JWT token OR session cookies
      const response = await fetch(`${getApiBaseUrl()}/profile/avatar`, {
        method: 'POST',
        headers,
        credentials: 'include', // Always include to send cookies for cross-origin requests
        body: formData,
      });
      
      // Handle empty response body (content-length: 0) on 401 errors
      let json;
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          json = await response.json();
        } else {
          // Empty body or non-JSON response
          const text = await response.text();
          json = text ? JSON.parse(text) : { message: `Server returned ${response.status} ${response.statusText}` };
        }
      } catch (parseError) {
        json = { message: `Server returned ${response.status} ${response.statusText}` };
      }
      
      if (!response.ok) {
        if (response.status === 401) {
          return rejectWithValue('Your session has expired. Please log in again.');
        }
        if (json.errors && Array.isArray(json.errors)) {
          return rejectWithValue(json.errors.join(', '));
        }
        return rejectWithValue(json.message || 'Failed to upload avatar');
      }
      return json.data;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Network error occurred while uploading avatar');
    }
  }
);

export const changePassword = createAsyncThunk<void, { oldPassword: string; newPassword: string }, { state: RootState }>(
  'profile/changePassword',
  async (data, { getState, rejectWithValue }) => {
    try {
      const state = getState().auth;
      const token = state.token;
      const authType = state.type;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      // Only add Authorization header for JWT auth
      if (token && authType === 'JWT') {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${getApiBaseUrl()}/profile/change-password`, {
        method: 'PUT',
        headers,
        credentials: authType === 'SESSION' ? 'include' : 'omit',
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const json = await response.json();
        if (json.errors) return rejectWithValue(json.errors.join(', '));
        return rejectWithValue(json.message || 'Failed to change password');
      }
      
      return;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Unknown error');
    }
  }
);

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearProfile: (state) => {
      state.profile = null;
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProfile.fulfilled, (state, action: PayloadAction<UserProfile>) => {
        state.isLoading = false;
        state.profile = action.payload;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action: PayloadAction<UserProfile>) => {
        state.isLoading = false;
        state.profile = action.payload;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearProfile } = profileSlice.actions;
export default profileSlice.reducer; 